import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppLayoutContextConsumer } from '@occc-app/design-system';
import { useGetCampaignName, getCreatePageLinks } from '@occc-app/routes/CampaignComponents/utils';
import { Offer_Engagement_Type } from '@occc/graphql';
import { OfferType } from '@occc-app/offerValidationService/types';

import { useChainId } from '@occc-app/Chain/hooks';
import { OfferCreateConfigurePage } from '@occc-app/routes/Offer/OfferCreatePage/OfferCreateConfigurePage';
import { Flow_Types } from '@occc-app/offerHelpers/types';
import { CampaignComponentTabs } from '@occc-app/routes/CampaignComponents/components/ConfigurationLayout';
import { useNavigateToCampaign } from '@occc-app/offerHelpers/hooks/useNavigateToCampaign';

import { messages } from '../messages';
import { routeCreator } from '../utils';
import { ConfigureForm } from '../components/PromotionConfigureForm/ConfigureForm';
import { getConfigureFormOperations } from '../components/PromotionConfigureForm/utils/configureFormOperations';
import { getOfferConfigureFormInitialValues } from '../components/PromotionConfigureForm/utils/configureFormInitialValues';
import { PromotionConfigureFormInitialValues, PromotionConfigureFormValues } from '../types';

export const PromotionCreatePageContainer = () => {
  const navigate = useNavigate();
  const { id: campaignId = '' } = useParams<{ id: string }>();

  const chainId = useChainId();
  const campaignName = useGetCampaignName(chainId, campaignId);
  const navigateToCampaign = useNavigateToCampaign(campaignId);

  const links = useMemo(
    () => getCreatePageLinks(campaignId, campaignName, 'New Promotion'),
    [campaignId, campaignName],
  );
  useAppLayoutContextConsumer(links);

  return (
    <OfferCreateConfigurePage<PromotionConfigureFormValues, PromotionConfigureFormInitialValues>
      pageTitle='Campaign > Create'
      subtitle={messages.subtitle}
      offerType={OfferType.Promotion}
      engagementType={Offer_Engagement_Type.Promotional}
      campaignId={campaignId}
      getConfigureOperations={getConfigureFormOperations}
      getInitialValues={getOfferConfigureFormInitialValues}
      onOfferCreated={(offerId) =>
        navigate(routeCreator.configureWithTab(campaignId, offerId, CampaignComponentTabs.Target))
      }
      renderConfigureForm={({ onSubmit, initialValues, loading }) => (
        <ConfigureForm
          onSubmit={onSubmit}
          initialValues={initialValues}
          loading={loading}
          onCancelClick={navigateToCampaign}
          currentFlowType={Flow_Types.Configure}
          isFormEditable
        />
      )}
    />
  );
};
